import { BasicNotepad, ICloudNotepadInfo, SpecialNotes } from "@fokused-me/core";
import {
   FMManageNotepadsDialog,
   FMSnackbars,
   ICreateNotepadInfo,
   IImportFileInputContentInfo,
   NotepadButtonActionType,
   NotepadContent,
   NotepadLinkInfo,
} from "@fokused-me/web-ui";
import { format } from "date-fns";
import { List, Map } from "immutable";
import { Fragment, useCallback, useState } from "react";
import { createPanelInfo, useDesktop } from "../state/Desktop";
import { notepadsToDialog, useNotepads } from "../state/Notepads";
import { useSession } from "../state/Session";
import { androidAppShell } from "../utils/environment";
import { exportNotes } from "../utils/exportNotes";
import { extractImportFileContent } from "../utils/extractImportFileContent";

const handlePasswordCheck = () => true;

export const ManageNotepadPage = ({ initMode, onClose }: { initMode: boolean; onClose?: () => void }) => {
   const {
      notepadInfos,
      notepadStates,
      notepads,
      createNotepad,
      updateNotepad,
      closeNotepad,
      openNotepad,
      deleteNotepad,
      getNotepadInfo,
      getCloudNotepadInfo,
   } = useNotepads();
   const { setNotepadCount } = useSession();
   const { panels, addPanel, closePanels } = useDesktop();

   const [linkCloudNotepadInfo] = useState<ICloudNotepadInfo>();

   const handleContinue = useCallback(() => {
      setNotepadCount(notepadInfos.size);
   }, [notepadInfos.size, setNotepadCount]);

   const handleImportFileResult = useCallback((fileName: string, fileContent: string) => {
      const notes = extractImportFileContent(fileContent);
      const notepad = new BasicNotepad(List(notes), Map());
      return {
         lastUpdate: notepad.getNoteUpdated(),
         noteCount: notepad.getNoteCount(),
      } as IImportFileInputContentInfo;
   }, []);

   const handleNotepadAction = useCallback(
      (notepadId: string, action: NotepadButtonActionType) => {
         switch (action) {
            case "export":
               // TODO notify that notes stored unencrypted in export file and special care should be taken
               const filename = `notes_${format(Date.now(), "yy-MM-dd_HHmmss")}.html`;
               const content = exportNotes(SpecialNotes.HOME, notepads.get(notepadId)!);
               download(content, filename, "text/html");
               break;
            case "delete":
            case "unlink":
               // close panels before deleting notepad
               const panelIds = (panels || List()).filter((it) => it.notepadId === notepadId).map((it) => it.id);
               if (panelIds && panelIds.size) {
                  closePanels(panelIds);
               }
               deleteNotepad(notepadId, action === "unlink");
               break;
            case "close":
               closeNotepad(notepadId).then(() => {
                  const panelsToClose = panels.filter((it) => it.notepadId === notepadId).map((it) => it.id);
                  if (!panelsToClose.isEmpty()) {
                     closePanels(panelsToClose);
                  }
               });
               break;
            case "open":
               openNotepad(notepadId).then(() => {
                  addPanel(
                     createPanelInfo({
                        notepadId,
                        rootNoteId: SpecialNotes.HOME,
                        type: "tree",
                     })
                  );
               });
               break;
         }
      },
      [notepads, panels, closePanels, deleteNotepad, closeNotepad, openNotepad, addPanel]
   );

   const handleCreateNotepad = useCallback(
      (createInfo: ICreateNotepadInfo) => {
         createNotepad({
            ...createInfo,
            name: createInfo.contentSource === NotepadContent.LINK ? linkCloudNotepadInfo?.name || "" : createInfo.name,
         }).then((info) => {
            const panel = createPanelInfo({
               notepadId: info.id,
               rootNoteId: SpecialNotes.HOME,
               type: "tree",
            });
            addPanel(panel);
         });
      },
      [linkCloudNotepadInfo, createNotepad, addPanel]
   );

   const handleNameUpdated = useCallback(
      (notepadId: string, newName: string) => updateNotepad(notepadId, { name: newName }),
      [updateNotepad]
   );

   const handleGetLinkInfoValue = useCallback(
      (notepadId: string) => {
         const info = getNotepadInfo(notepadId);
         return notepadId + (info?.type === "cloud" ? info.encryptKey : "");
      },
      [getNotepadInfo]
   );

   const { setSnackbar } = FMSnackbars.useContainer();

   const handleVerifyLinkInfo = useCallback(
      (link: string) => {
         const notepadId = link.substr(0, 64);
         const accessKey = link.substr(64);

         const emptyResult = () => {
            setSnackbar({
               severity: "error",
               message: "Invalid Cloud notepad link!",
            });

            return Promise.resolve(undefined);
         };

         if (!notepadId || !accessKey) {
            console.log("Doesn't look like valid notepadId / accessKey pair");
            return emptyResult();
         }

         return getCloudNotepadInfo(notepadId, accessKey)
            .then((info) => {
               let result: NotepadLinkInfo | undefined;

               if (info && info.name) {
                  result = {
                     accessKey,
                     notepadId,
                     name: info.name,
                  };

                  setSnackbar({
                     severity: "success",
                     message: "Successfully verified Cloud notepad link!",
                  });

                  return result;
               } else {
                  console.log("Couldn't access notepad");
                  return emptyResult();
               }
            })
            .catch((e) => {
               console.log("Getting notepad info failed!", e);
               return emptyResult();
            });
      },
      [getCloudNotepadInfo, setSnackbar]
   );

   const handleCopyOrDownloadInfo = useCallback(
      (notepadId: string, doDownload: boolean) => {
         if (doDownload) {
            const linkInfo = handleGetLinkInfoValue(notepadId);
            const info = getNotepadInfo(notepadId);
            download(linkInfo, info!.name.replaceAll(/\s+/g, "_") + "-fokused-me.cnlink");
         }
      },
      [handleGetLinkInfoValue, getNotepadInfo]
   );

   return (
      <Fragment>
         <FMManageNotepadsDialog
            initMode={initMode}
            notepads={notepadsToDialog(notepadInfos, notepadStates)}
            onCreateNotepad={handleCreateNotepad}
            onCurrentPasswordCheck={handlePasswordCheck}
            onNameUpdated={handleNameUpdated}
            onNotepadAction={handleNotepadAction}
            onImportFileResult={handleImportFileResult}
            onImportFileError={console.log}
            onCopyOrDownloadInfo={handleCopyOrDownloadInfo}
            onGetLinkInfoValue={handleGetLinkInfoValue}
            onVerifyLinkInfo={handleVerifyLinkInfo}
            onContinue={initMode ? handleContinue : onClose}
         />
      </Fragment>
   );
};

function download(content: string, filename: string, contentType?: string) {
   if (androidAppShell) {
      // TODO enable snakbar for Android Shell
      if (androidAppShell.saveFile(filename, content)) {
         // createSnackbar({ text: `Saved file ${filename} to Downloads` });
      } else {
         // createSnackbar({ text: `Couldn't save file`, variant: "error" });
      }
   } else {
      if (!contentType) {
         contentType = "application/octet-stream";
      }
      const a = document.createElement("a");
      const blob = new Blob([content], { type: contentType });
      a.href = window.URL.createObjectURL(blob);
      a.download = filename;
      a.click();
   }
}
